import React, { useState } from "react";
import {
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  Avatar,
  Grid,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { deleteFile } from "Utils/fileServices";
import { useTranslation } from "react-i18next";
import config from "../../../config";
import { MAX_FILE_SIZE_MB } from "Utils/fileValidation";

interface UploadedFile {
  filename: string;
  url: string;
}

interface MultiFileSelectorProps {
  maxFiles?: number;
  maxFileSizeMB?: number;
  onFilesChange?: (files: UploadedFile[]) => void;
  onPendingFilesChange?: (files: File[]) => void;
  existedFiles?: any;
}

const allowedTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "video/mp4",
];

const MultiFileSelector: React.FC<MultiFileSelectorProps> = ({
  maxFiles = 5,
  maxFileSizeMB = MAX_FILE_SIZE_MB,
  onFilesChange,
  onPendingFilesChange,
  existedFiles,
}) => {
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(existedFiles || []);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>("");

  const isImage = (type: string) => type.startsWith("image/");
  const isVideo = (type: string) => type.startsWith("video/");

  const getFileIcon = (type: string) => {
    if (type.includes("pdf")) return <PictureAsPdfIcon />;
    if (type.includes("word")) return <DescriptionIcon />;
    return <InsertDriveFileIcon />;
  };

  const validateFiles = (files: FileList | File[]) => {
    const filesArray = Array.from(files);
    if (filesArray.length + uploadedFiles.length > maxFiles) {
      return `You can select up to ${maxFiles} files only.`;
    }
    for (const file of filesArray) {
      if (!allowedTypes.includes(file.type)) {
        return `File type ${file.type} is not allowed.`;
      }
      if (file.size > maxFileSizeMB * 1024 * 1024) {
        return `File "${file.name}" exceeds ${maxFileSizeMB}MB size limit.`;
      }
    }
    return "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    const files = e.target.files;
    if (!files) return;

    const filesArray = Array.from(files);
    const validationError = validateFiles(filesArray);
    if (validationError) {
      setError(validationError);
      return;
    }

    const updatedPendingFiles = [...pendingFiles, ...filesArray];
    setPendingFiles(updatedPendingFiles);
    onPendingFilesChange && onPendingFilesChange(updatedPendingFiles);

    e.target.value = "";
  };

  const handleDeleteUploaded = async (fileToRemove: UploadedFile) => {
    try {
      await deleteFile(fileToRemove.filename);
      const updatedFiles = uploadedFiles.filter(
        (file) => file.filename !== fileToRemove.filename
      );
      setUploadedFiles(updatedFiles);
      onFilesChange && onFilesChange(updatedFiles);
    } catch (err: any) {
      setError(err?.message || "Failed to delete file");
    }
  };

  const handleDeletePending = (index: number) => {
    const updated = pendingFiles.filter((_, i) => i !== index);
    setPendingFiles(updated);
    onPendingFilesChange && onPendingFilesChange(updated);
  };

  const renderPreview = (file: any, isUploaded = false) => {
    let url = isUploaded ? file.url : URL.createObjectURL(file);
    if (isUploaded && url && typeof url === 'string' && !url.startsWith('data:image') && url.includes('/file/')) {
      const filename = url.split('/').pop();
      url = `${config.NODE_API_BASE_URL_V2}/file/${filename}`;
    }
    let type = isUploaded ? "" : file.type;
    if (isUploaded && file.filename) {
      const ext = file.filename.split('.').pop()?.toLowerCase();
      if (['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp'].includes(ext || '')) {
        type = "image/uploaded";
      } else if (['mp4', 'webm', 'ogg'].includes(ext || '')) {
        type = "video/uploaded";
      }
    }

    if (isImage(type) || (isUploaded && typeof url === 'string' && url.startsWith('data:image'))) {
      return <img src={url} alt="preview" style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8 }} />;
    }

    if (isVideo(type)) {
      return (
        <video
          src={url}
          style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8 }}
          controls
        />
      );
    }

    return (
      <Box
        sx={{
          height: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
          borderRadius: 2,
          cursor: "pointer",
        }}
        onClick={() => window.open(url, "_blank")}
      >
        {getFileIcon(type)}
      </Box>
    );
  };

  const getDisplayName = (name: string) => {
    return name.includes("-") ? name.split("-").slice(1).join("-") : name;
  };

  const totalFileCount = uploadedFiles.length + pendingFiles.length;

  return (
    <Box>
      <Typography fontSize={12} color="text.secondary" mb={1}>
        Up to {maxFiles} files, {maxFileSizeMB}MB each. Accepted: PDF, DOC, DOCX, JPG, PNG, MP4.
        {" "}({totalFileCount}/{maxFiles} files selected)
      </Typography>
      <Button
        variant="contained"
        component="label"
        startIcon={<UploadFileIcon />}
        sx={{
          mb: 2,
          textTransform: "capitalize",
          backgroundColor: "#00D084",
          borderRadius: "32px",
          px: 4,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#00b873",
            boxShadow: "none",
          },
        }}
      >
        {t('common.select_files')}
        <input
          hidden
          type="file"
          multiple
          accept={allowedTypes.join(",")}
          onChange={handleFileChange}
        />
      </Button>

      {(uploadedFiles.length > 0 || pendingFiles.length > 0) && (
        <Grid container spacing={2}>
          {uploadedFiles.map((file) => (
            <Grid size={{ xs: 12, sm: 4, md: 4 }} key={file.filename}>
              <Paper sx={{ p: 1.5, borderRadius: 3 }}>
                {renderPreview(file, true)}

                <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                  <Tooltip title={file.filename}>
                    <Typography noWrap fontSize={13} fontWeight={600}>
                      {getDisplayName(file.filename)}
                    </Typography>
                  </Tooltip>

                  <IconButton size="small" onClick={() => handleDeleteUploaded(file)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography fontSize={11} color="green">
                  {t('common.uploaded')}
                </Typography>
              </Paper>
            </Grid>
          ))}

          {pendingFiles.map((file, index) => (
            <Grid size={{ xs: 12, sm: 4, md: 4 }} key={index}>
              <Paper sx={{ p: 1.5, borderRadius: 3 }}>
                {renderPreview(file)}

                <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                  <Tooltip title={file.name}>
                    <Typography noWrap fontSize={13} fontWeight={600}>
                      {file.name}
                    </Typography>
                  </Tooltip>

                  <IconButton size="small" onClick={() => handleDeletePending(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography fontSize={11} color="orange">
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {error && (
        <Typography color="error" mt={1}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default MultiFileSelector;
